
import React, { useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import WaterStatCards from './WaterStatCards';
import WaterConsumptionBarChart from './WaterConsumptionBarChart';
import WaterDistributionPieChart from './WaterDistributionPieChart';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BrainCircuit, Droplet, AlertCircle, WavesIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';

const WaterAIAdvisoryTab = () => {
  const [activeSubTab, setActiveSubTab] = useState("advisories");

  const waterAdvisories = [
    {
      id: 1,
      title: "Potential Leak Detection",
      description: "Continuous low-level flow detected during non-usage hours in zone 3. Recommend investigation for possible underground leakage.",
      impact: "High",
      category: "Leak"
    },
    {
      id: 2,
      title: "Water Quality Fluctuation",
      description: "pH levels have varied by 0.8 in the past 48 hours. Consider adjusting treatment processes to stabilize quality metrics.",
      impact: "Medium",
      category: "Quality"
    },
    {
      id: 3,
      title: "Consumption Pattern Anomaly",
      description: "Office building 5 usage has increased 35% despite occupancy remaining stable. Check for inefficient fixtures or possible internal leaks.",
      impact: "Medium",
      category: "Usage"
    },
    {
      id: 4,
      title: "Pressure Management",
      description: "Pressure spikes detected during off-peak hours. Adjusting pressure reducing valves could improve system efficiency and reduce stress on infrastructure.",
      impact: "Low",
      category: "Pressure"
    }
  ];

  return (
    <div className="h-full">
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="h-full flex flex-col">
        <div className="flex-none">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="advisories" className="flex items-center gap-1 text-xs py-1">
              <BrainCircuit className="h-3 w-3" />
              <span className="hidden sm:inline">Advisories</span>
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-1 text-xs py-1">
              <Droplet className="h-3 w-3" />
              <span className="hidden sm:inline">Analytics</span>
            </TabsTrigger>
            <TabsTrigger value="distribution" className="flex items-center gap-1 text-xs py-1">
              <Droplet className="h-3 w-3" />
              <span className="hidden sm:inline">Distribution</span>
            </TabsTrigger>
          </TabsList>
        </div>

        <div className="flex-1 overflow-hidden mt-2">
          <TabsContent value="advisories" className="h-full overflow-auto m-0">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <BrainCircuit className="h-5 w-5 text-cyan-500" />
                  Water System AI Advisories
                </h2>
                <Button variant="outline" size="sm">Export Insights</Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {waterAdvisories.map(advisory => (
                  <Card key={advisory.id} className="border-l-4 border-l-cyan-500">
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <CardTitle className="text-lg font-medium flex items-center gap-2">
                          {advisory.category === "Leak" && <Droplet className="h-4 w-4 text-cyan-500" />}
                          {advisory.category === "Quality" && <AlertCircle className="h-4 w-4 text-purple-500" />}
                          {advisory.category === "Usage" && <AlertCircle className="h-4 w-4 text-blue-500" />}
                          {advisory.category === "Pressure" && <AlertCircle className="h-4 w-4 text-orange-500" />}
                          {advisory.title}
                        </CardTitle>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          advisory.impact === "High" ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" : 
                          advisory.impact === "Medium" ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300" : 
                          "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                        }`}>
                          {advisory.impact} Impact
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 dark:text-gray-300">{advisory.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="analytics" className="h-full overflow-auto m-0">
            <WaterStatCards />
          </TabsContent>

          <TabsContent value="distribution" className="h-full overflow-auto m-0">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <WaterConsumptionBarChart />
              <WaterDistributionPieChart />
            </div>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

export default WaterAIAdvisoryTab;
